import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import $ from 'jquery';
import Select from 'react-select';
import { fetchData } from '../utils/apiHelpers';
import { getAll, create, update, deleteOne } from '../api';

const TechniciansLayer = () => {
  const [technicians, setTechnicians] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [trades, setTrades] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedTrades, setSelectedTrades] = useState([]);

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  useEffect(() => {
    fetchData(getAll, { apiName: 'technicians' }, setTechnicians);
    fetchData(getAll, { apiName: 'users' }, setUsers);
    fetchData(getAll, { apiName: 'teams' }, setTeams);
    fetchData(getAll, { apiName: 'trades' }, setTrades);
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy(true);
      dtRef.current = null;
    }
    if (technicians.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        columnDefs: [{ orderable: false, targets: 0 }],
        info: false,
        responsive: false,
        language: { paginate: { first: '', last: '', next: '', previous: '' } }
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [technicians]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const handleAdd = () => {
    setEditingId(null);
    setSelectedUser(null);
    setSelectedTeams([]);
    setSelectedTrades([]);
    setShowModal(true);
  };

  const handleEdit = (tech) => {
    setEditingId(tech.id);
    if (tech.users) {
      setSelectedUser({ value: tech.users.id, label: `${tech.users.name || ''} (${tech.users.email})` });
    }
    setSelectedTeams(
      (tech.teams || []).map((id) => {
        const t = teams.find((tm) => tm.id === id);
        return t ? { value: t.id, label: t.teamName } : null;
      }).filter(Boolean)
    );
    setSelectedTrades(
      (tech.trades || []).map((id) => {
        const tr = trades.find((td) => td.id === id);
        return tr ? { value: tr.id, label: tr.name } : null;
      }).filter(Boolean)
    );
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this technician?')) return;
    try {
      await fetchData(deleteOne, { apiName: 'technicians', id });
      const tech = technicians.find((t) => t.id === id);
      if (tech) {
        await update('users', tech.userId, { assignable: 0 });
      }
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      fetchData(getAll, { apiName: 'technicians' }, setTechnicians);
      fetchData(getAll, { apiName: 'users' }, setUsers);
    } catch (err) {
      console.error('Error deleting technician:', err);
    }
  };

  const handleSave = async () => {
    const currentTech = technicians.find((t) => t.id === editingId);
    const userId = selectedUser ? selectedUser.value : currentTech?.userId;

    if (!userId || selectedTeams.length === 0) {
      if (!selectedUser || selectedTeams.length === 0) {
        alert('Please select a user and at least one team');
        return;
      }
    }

    const payload = {
      userId,
      teams: selectedTeams.map((t) => t.value),
      trades: selectedTrades.map((t) => t.value),
      active: 1
    };

    try {
      if (editingId) {
        await update('technicians', editingId, payload);
      } else {
        await create('technicians', payload);
      }
      await update('users', userId, { assignable: 1 });
      setShowModal(false);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      fetchData(getAll, { apiName: 'technicians' }, setTechnicians);
      fetchData(getAll, { apiName: 'users' }, setUsers);
    } catch (err) {
      console.error('Error saving technician:', err);
    };
  }

  const selectableUsers = users.filter((u) => u.assignable === 0 && u.active === 1);

  return (
    <div className='card h-100 p-0 rounded-0 overflow-hidden'>
      <div className='card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between'>
        <input
          type='text'
          className='bg-base form-control w-auto'
          placeholder='Search technicians…'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button className='btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2' onClick={handleAdd}>
          <Icon icon='ic:baseline-plus' className='icon text-xl line-height-1' />
          Add Technician
        </button>
      </div>
      <div className='card-body p-0'>
        <div className='table-responsive'>
          <table className='table bordered-table mb-3' ref={tableRef}>
            <thead>
              <tr>
                <th></th>
                <th>Number</th>
                <th>Name</th>
                <th>Email</th>
                <th>Teams</th>
                <th>Trades</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {technicians.map((tech) => {
                return (
                  <tr key={tech.id}>
                    <td style={{ width: '77px' }}>
                      {(
                          <img
                          src={
                              tech.users.userImage === null || tech.users.userImage === ''
                                ? `${process.env.REACT_APP_SPACES_BASE}/user.png`
                                : `${process.env.REACT_APP_SPACES_BASE}/${tech.users.userImage}`
                            }
                          alt="User"
                          style={{
                              width: '45px',
                              height: '45px',
                              objectFit: 'cover',
                              borderRadius: '50%', // Rounded square
                              border: '1px solid #ddd',
                          }}
                          />
                      )}
                    </td>
                    <td>{tech.users.payrollNumber}</td>
                    <td>{tech.users.name}</td>
                    <td>{tech.users.email}</td>
                    <td>
                      {(tech.teams || []).map((id) => {
                        const t = teams.find((tm) => tm.id === id);
                        return t ? (
                          <span key={id} className='text-secondary-light border radius-4 px-8 my-4 text-sm fw-medium mx-2'>
                            {t.teamName}
                          </span>
                        ) : null;
                      })}
                    </td>
                    <td>
                      {(tech.trades || []).map((id) => {
                        const tr = trades.find((td) => td.id === id);
                        return tr ? (
                          <span key={id} className='text-secondary-light border radius-4 px-8 my-4 text-sm fw-medium mx-2'>
                            {tr.name}
                          </span>
                        ) : null;
                      })}
                    </td>
                    <td>
                      <div className='d-flex gap-2'>
                        <button className='w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center' onClick={() => handleEdit(tech)}>
                          <Icon icon='ic:baseline-edit' className='icon text-xl line-height-1' />
                        </button>
                        <button className='w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center' onClick={() => handleDelete(tech.id)}>
                          <Icon icon='ic:baseline-delete-outline' className='icon text-xl line-height-1' />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {technicians.length === 0 && (
                <tr>
                  <td colSpan={7} className='text-center py-2'>
                    No Technicians items found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {showModal && (
        <div className='modal show d-block' tabIndex='-1'>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>{editingId ? 'Edit Technician' : 'Add Technician'}</h5>
                <button type='button' className='btn-close' onClick={() => setShowModal(false)} />
              </div>
              <div className='modal-body'>
                {!editingId && (
                  <div className='mb-3'>
                    <label className='form-label'>Select User *</label>
                    <Select
                      value={selectedUser}
                      onChange={setSelectedUser}
                      options={selectableUsers.map((u) => ({ value: u.id, label: `${u.name || ''} (${u.email})` }))}
                    />
                  </div>
                )}
                {(() => {
                  const currentTech = technicians.find((t) => t.id === editingId);
                  if (!currentTech) return null;
                  return currentTech ? (
                    <>
                      <div className="mb-3 mx-3 text-center">
                        <img
                          src={`${process.env.REACT_APP_SPACES_BASE}/${currentTech.users.userImage}`}
                          alt="User"
                          style={{
                            width: '150px',
                            height: '150px',
                            objectFit: 'cover',
                            borderRadius: '50%',
                            border: '1px solid #ccc',
                          }}
                        />
                      </div>
                      <div className='mb-3'>
                        <label className='form-label'>Name</label>
                        <input className='form-control' value={currentTech.users.name || ''} readOnly />
                      </div>
                      <div className='mb-3'>
                        <label className='form-label'>Email</label>
                        <input className='form-control' value={currentTech.users.email || ''} readOnly />
                      </div>
                    </>
                  ) : null;
                })()}
                <div className='mb-3'>
                  <label className='form-label'>Teams *</label>
                  <Select isMulti value={selectedTeams} onChange={setSelectedTeams} options={teams.map((t) => ({ value: t.id, label: t.teamName }))} />
                </div>
                <div className='mb-3'>
                  <label className='form-label'>Trades</label>
                  <Select isMulti value={selectedTrades} onChange={setSelectedTrades} options={trades.map((t) => ({ value: t.id, label: t.name }))} />
                </div>
              </div>
              <div className='modal-footer'>
                <button className='btn btn-secondary' onClick={() => setShowModal(false)}>Close</button>
                <button className='btn btn-primary' onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechniciansLayer;
